import { useState, useCallback, Suspense, lazy } from "react";
import "./styles/globals.css";

import { ErrorBoundary }       from "./components/ErrorBoundary";
import { SkeletonLoader }      from "./components/SkeletonLoader";
import { Navbar }              from "./components/layout/Navbar";
import { Footer }              from "./components/layout/Footer";
import { ChatbotWidget }       from "./components/modals/ChatbotWidget";
import { AuthModal }           from "./components/modals/AuthModal";
import { ToastStack }          from "./components/modals/ToastStack";

import { useToast }            from "./hooks/useToast";

// Lazy loaded components
const HomePage = lazy(() => import("./pages/HomePage").then(module => ({ default: module.HomePage })));
const BrowsePage = lazy(() => import("./pages/BrowsePage").then(module => ({ default: module.BrowsePage })));
const GameDetailPage = lazy(() => import("./pages/GameDetailPage").then(module => ({ default: module.GameDetailPage })));
const WishlistPage = lazy(() => import("./pages/WishlistPage").then(module => ({ default: module.WishlistPage })));
const DealsPage = lazy(() => import("./pages/DealsPage").then(module => ({ default: module.DealsPage })));
const NewReleasesPage = lazy(() => import("./pages/NewReleasesPage").then(module => ({ default: module.NewReleasesPage })));
const PublisherPortalPage = lazy(() => import("./pages/PublisherPortalPage").then(module => ({ default: module.PublisherPortalPage })));
const AccountPage = lazy(() => import("./pages/AccountPage").then(module => ({ default: module.AccountPage })));

type Page =
  | "home" | "browse" | "detail" | "wishlist"
  | "deals" | "new-releases" | "publisher" | "account"
  | "signin" | "signup";

export default function App() {
  const [page, setPage]               = useState<Page>("home");
  const [selectedId, setSelectedId]   = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeGenre, setActiveGenre] = useState("");
  const [authMode, setAuthMode]       = useState<"signin" | "signup">("signin");

  const { toasts, dismiss } = useToast();

  const navigate = useCallback((p: string) => {
    setPage(p as Page);
    if (p !== "detail") setSelectedId(null);
    if (p !== "browse") { setSearchQuery(""); setActiveGenre(""); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openGame = useCallback((id: string) => {
    setSelectedId(id);
    setPage("detail");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q); setActiveGenre(""); setPage("browse");
  }, []);

  const handleGenre = useCallback((genre: string) => {
    setActiveGenre(genre); setSearchQuery(""); setPage("browse");
  }, []);

  const showAuth = page === "signin" || page === "signup";

  return (
    <>
      <Navbar page={page} setPage={navigate} onSearch={handleSearch} />

      <main style={{ minHeight: "calc(100vh - 62px)" }}>
        <ErrorBoundary>
          <Suspense fallback={<SkeletonLoader type="page" />}>
            {page === "home"         && <HomePage onSelect={openGame} onGenre={handleGenre} onNavigate={navigate} />}
            {page === "browse"       && <BrowsePage onSelect={openGame} initialSearch={searchQuery} initialGenre={activeGenre} />}
            {page === "detail" && selectedId && <GameDetailPage gameId={selectedId} onBack={() => navigate("home")} />}
            {page === "wishlist"     && <WishlistPage onSelect={openGame} onBrowse={() => navigate("browse")} />}
            {page === "deals"        && <DealsPage onSelect={openGame} />}
            {page === "new-releases" && <NewReleasesPage onSelect={openGame} />}
            {page === "publisher"    && <PublisherPortalPage />}
            {page === "account"      && <AccountPage onSelect={openGame} onBrowse={() => navigate("browse")} />}
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />
      {showAuth && <AuthModal mode={authMode} onClose={() => navigate("home")} onSwitch={(m) => { setAuthMode(m); setPage(m); }} />}
      <ChatbotWidget />
      <ToastStack toasts={toasts} dismiss={dismiss} />
    </>
  );
}
